import type { ClientDocument } from "@/features/documents/types";

export type FormCategory =
  | "COLLECTION_INFO"
  | "POA_AUTHORIZATION"
  | "PAYMENT_PLAN"
  | "OFFER_IN_COMPROMISE"
  | "RELIEF"
  | "APPEALS"
  | "OTHER";

export const CATEGORY_LABEL: Record<FormCategory, string> = {
  POA_AUTHORIZATION: "Power of Attorney",
  COLLECTION_INFO: "Collection Information Statements",
  PAYMENT_PLAN: "Installment Agreements",
  OFFER_IN_COMPROMISE: "Offer in Compromise",
  RELIEF: "Relief",
  APPEALS: "Appeals",
  OTHER: "Other",
};

export type FormJurisdiction = "FEDERAL" | "STATE";

export interface TaxFormDef {
  id: string;
  formNumber: string;
  title: string;
  category: FormCategory;
  jurisdiction: FormJurisdiction;
  appliesToIndividuals: boolean;
  appliesToBusinesses: boolean;
  isActive: boolean;
}

/** A user's favorited form (library heart toggle). */
export interface FormFavorite {
  id: string;
  taxForm: string;
  formNumber: string;
  formTitle: string;
}

/** Optional Save-to-Documents parameters for the generate action. */
export interface GenerateParams {
  documentName?: string;
  folderId?: string | null;
  overwrite?: boolean;
}

export type FormStatus = "NOT_STARTED" | "DRAFT" | "COMPLETED";

export interface FormSubmission {
  id: string;
  client: string;
  clientName: string;
  case: string | null;
  taxForm: string;
  formNumber: string;
  formTitle: string;
  data: Record<string, unknown>;
  status: FormStatus;
  statusDisplay: string;
  generatedDocument: ClientDocument | null;
  generatable: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form numbers with a backend PDF-fill implementation (mirrors the backend's
 * `apps/forms/services._MAPPERS`). Used to enable the Generate button for a form
 * that has no submission yet; the backend re-checks and 400s otherwise.
 */
export const GENERATABLE_FORMS = new Set<string>([
  "2848",
  "3520",
  // Injured Spouse Allocation — driven by the ISA wizard (milestone 03-15).
  "8379",
  // Request for Innocent Spouse Relief — driven by the Spouse-Relief wizard
  // (milestone 03-16); the wizard is also the plain 8857 editor.
  "8857",
  // 433 financial-statement family (milestone 03-1).
  "433-A",
  "433-A (SP)",
  "433-A (OIC)",
  "433-B",
  "433-B (SP)",
  "433-B (OIC)",
  "433-D",
  "433-D (SP)",
  "433-F",
  "433-F (SP)",
  "433-H",
  "433-H (SP)",
  // Installment-Agreement request family (milestone 03-3). The Spanish twins
  // (9465sp / 2159sp) are deferred pending their blank IRS PDF templates.
  "9465",
  "2159",
  // Power of Attorney family (milestone 03-2): federal 8821 / Spanish 2848 and
  // the state POA/TIA variants.
  "8821",
  "2848sp",
  "2848a",
  "2848dc",
  "2848il",
  "2848ri",
  "ia2848",
  "m2848",
  "sc2848",
  "wv2848",
  "poa1",
  "par101",
  "idpoa",
  "mipoa",
  "orauthrep",
  "orauthinfo",
]);

/**
 * The 433 forms that have their own editable form (payment terms / IA header);
 * every other 433 form is "attachment-style" — no field entry, its data comes
 * from the worksheet layer and the fill page only previews / saves the PDF.
 */
export const FORMS_433_WITH_EDITOR = new Set<string>([
  "433-D",
  "433-D (SP)",
  "433-H",
  "433-H (SP)",
]);

/** 433 forms that compute an OIC offer (show the offer-plan selector). */
export const FORMS_433_OIC = new Set<string>(["433-A (OIC)", "433-B (OIC)"]);

// --- Form 2848 editable payload ---------------------------------------------
// The submission's `data` blob is passed through verbatim (no snake→camel
// mapping), so these keys match the backend mapper's `build_defaults` exactly.

export type GenerateFor = "taxpayer" | "spouse" | "jointly";

/**
 * Mirrors the backend Practitioner.DESIGNATION_CHOICES — the 11 IRS Form 2848
 * Part II designation letter codes (stored verbatim as the letter).
 */
export type PractitionerDesignation =
  | "a" | "b" | "c" | "d" | "e" | "f" | "g" | "h" | "i" | "k" | "r";

export const DESIGNATION_OPTIONS: { value: PractitionerDesignation; label: string }[] = [
  { value: "a", label: "Attorney" },
  { value: "b", label: "Certified Public Accountant" },
  { value: "c", label: "Enrolled Agent" },
  { value: "d", label: "Officer" },
  { value: "e", label: "Full-Time Employee" },
  { value: "f", label: "Family Member" },
  { value: "g", label: "Enrolled Actuary" },
  { value: "h", label: "Unenrolled Return Preparer" },
  { value: "i", label: "Registered Tax Return Preparer" },
  { value: "k", label: "Student Attorney or CPA" },
  { value: "r", label: "Enrolled Retirement Plan Agent" },
];

export type ReturnOwnership =
  | ""
  | "individual"
  | "corporation"
  | "partnership"
  | "fiduciary"
  | "estate"
  | "trust"
  | "llc";

export const RETURN_OWNERSHIP_OPTIONS: { value: ReturnOwnership; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "corporation", label: "Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "fiduciary", label: "Fiduciary" },
  { value: "estate", label: "Estate" },
  { value: "trust", label: "Trust" },
  { value: "llc", label: "Limited Liability Company" },
];

export interface Form2848Representative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
}

export interface Form2848Matter {
  tax_type: string;
  description: string;
  tax_form: string;
  years: string;
  return_ownership: ReturnOwnership;
}

export interface Form2848ActsAuthorized {
  access_records: boolean;
  disclose: boolean;
  substitute: boolean;
  sign_return: boolean;
  specific_use: boolean;
}

export interface Form2848Data {
  generate_for: GenerateFor;
  representatives: Form2848Representative[];
  matters: Form2848Matter[];
  acts_authorized: Form2848ActsAuthorized;
  exceptions: string;
}

/** The template's Part I tax-matters table has three body rows. */
export const MAX_MATTERS = 3;

// --- Form 8821 (Tax Information Authorization) editable payload --------------
// snake_case keys mirror `apps/forms/mappings/form_8821.py` `build_defaults`.
// 8821 is disclosure-only: appointees (not representatives), no acts/exceptions.

export interface Form8821Appointee {
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
  ok_send: boolean;
  new_address: boolean;
  new_phone: boolean;
  new_fax: boolean;
}

export interface Form8821Matter {
  /** Type of tax information (8821 Section 3 column a). */
  tax_type: string;
  tax_form: string;
  tax_years: string;
  /** Specific tax matters (column d). */
  tax_matters: string;
}

export interface Form8821Data {
  generate_for: "taxpayer" | "spouse";
  representatives: Form8821Appointee[];
  matters: Form8821Matter[];
  auth_access: boolean;
  non_caf: boolean;
  dont_revoke: boolean;
  signer: string;
  signer_title: string;
  member_ssn: string;
  plan_no: string;
}

/** The 8821 template's Section 3 table has three body rows. */
export const MAX_TIA_MATTERS = 3;

// --- Form 3520 (CA FTB POA) editable payload --------------------------------
// snake_case keys mirror the backend `apps/forms/mappings/form_3520.py` `data`.

export interface Form3520Representative {
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
}

export type AuthorizationMode = "all" | "specific";

export interface Form3520YearRange {
  from: string;
  to: string;
}

export interface Form3520Authorization {
  mode: AuthorizationMode;
  years: Form3520YearRange[];
}

export interface Form3520Privileges {
  add_rep: boolean;
  sign_absent: boolean;
  receive_check: boolean;
  waive_sol: boolean;
  exec_settlements: boolean;
  other_priv: boolean;
  online_access: boolean;
}

export interface Form3520Data {
  for_who: "taxpayer" | "business";
  representatives: Form3520Representative[];
  authorization: Form3520Authorization;
  privileges: Form3520Privileges;
  other_acts: string;
  ca_corp_no: string;
  ca_sos_no: string;
  contact_name: string;
  contact_title: string;
}

// --- Form 8379 (Injured Spouse Allocation) editable payload -----------------
// snake_case keys mirror the backend `apps/forms/mappings/form_8379.py` `data`
// exactly (passed verbatim, no snake→camel). Drives the ISA wizard (03-15); the
// preparer/firm block + spouse_phone are auto-filled server-side, not typed here.

/** Yes/No question answer (Part I Q2–Q9, Part II Q12). "" = unanswered. */
export type YesNo = "Y" | "N" | "";

/** Part III allocation column keys. */
export type Form8379AllocationRow =
  | "inc_rep"
  | "all_oth_inc"
  | "adj_inc"
  | "std_ded"
  | "num_exmp"
  | "credit"
  | "oth_tax"
  | "fed_tax"
  | "payment";

export interface Form8379Data {
  // Part I — Eligibility
  tax_year: string;
  file_joint_ret: YesNo;
  joint_overpayment: YesNo;
  legally_oblig_pay: YesNo;
  resd_comm_prop_chk: YesNo;
  res_comm_prop: string; // conditional (Q5 = Y)
  make_report_pay: YesNo;
  earned_income: YesNo;
  claim_earn_inc: YesNo;
  claim_refund_tax: YesNo;
  // Part II — Injured-spouse information
  sp_name1: string;
  sp_ssn1: string;
  sp_injure_chk: boolean;
  sp_name2: string;
  sp_ssn2: string;
  sp_injure_chk2: boolean;
  ref_both_name: boolean;
  sp_ref_add_diff: YesNo;
  sp_diff_add: string; // conditional (Q12 = Y)
  self_emp: boolean;
  // Part III — Allocation grid (9 rows × 3 columns; num_exmp is an integer count)
  inc_rep_jnt_ret: string; inc_rep_inj_sp: string; inc_rep_oth_sp: string;
  all_oth_inc_jnt_ret: string; all_oth_inc_inj_sp: string; all_oth_inc_oth_sp: string;
  adj_inc_jnt_ret: string; adj_inc_inj_sp: string; adj_inc_oth_sp: string;
  std_ded_jnt_ret: string; std_ded_inj_sp: string; std_ded_oth_sp: string;
  num_exmp_jnt_ret: string; num_exmp_inj_sp: string; num_exmp_oth_sp: string;
  credit_jnt_ret: string; credit_inj_sp: string; credit_oth_sp: string;
  oth_tax_jnt_ret: string; oth_tax_inj_sp: string; oth_tax_oth_sp: string;
  fed_tax_jnt_ret: string; fed_tax_inj_sp: string; fed_tax_oth_sp: string;
  payment_jnt_ret: string; payment_inj_sp: string; payment_oth_sp: string;
}

// --- Form 8857 (Request for Innocent Spouse Relief) editable payload ---------
// snake_case keys mirror the backend `apps/forms/mappings/form_8857.py` `data`
// exactly (passed verbatim, no snake→camel). Drives the Spouse-Relief wizard
// (03-16). The requesting person's own name/SSN + default address and the
// preparer/firm block are auto-filled server-side, not typed here. Part IV assets
// live in the shared worksheet asset store (not this blob).

/** Current marital status (Part II Q7). "" = unanswered. */
export type MaritalStatus8857 = "M" | "A" | "W" | "S" | "D" | "";
/** Highest education level (Part II Q8). "" = unanswered. */
export type Education8857 = "N" | "D" | "C" | "H" | "";

export interface Form8857Data {
  // Part I — Threshold conditions & tax years
  para_desc: YesNo;
  irs_take_share: YesNo;
  tax_year1: string; tax_year2: string; tax_year3: string;
  tax_year4: string; tax_year5: string; tax_year6: string;
  // Part II — Requesting person & spouse identity
  prefer_english: YesNo;
  other_language: string; // conditional (Q4 = N)
  diff_add_chk: boolean;
  diff_add1: string; // conditional (diff_add_chk)
  curr_spouce_apt: string;
  curr_spouce_county: string;
  diff_add2: string;
  client_day_time_phone: string;
  irs_leave_voicemail: boolean;
  curr_spouce: string;
  curr_spouce_ssn: string; // PII — masked input, 9-digit server validation
  curr_spouce_add1: string;
  curr_spouce_apt2: string;
  curr_spouce_add2: string;
  sp_day_time_phone: string;
  marital_status: MaritalStatus8857;
  married_apart_since: string; // conditional (A)
  widowed_since: string; // conditional (W)
  ligally_separate_since: string; // conditional (S)
  divorce_since: string; // conditional (D)
  high_education: Education8857;
  list_degree: string; // conditional (H)
  business_course: string;
  education_explain: string;
  mental_physical_health: YesNo;
  health_explain: string;
  afraid_to_provide: YesNo;
  // Part III — Narrative / knowledge & asset transfer
  file_joint_return: YesNo;
  file_return_explain: string;
  inv_prep_ret_exp: string;
  person_income_know_exp: string;
  ret_missed_info_exp: string;
  show_bal_due_yes_exp: string;
  finance_problem_yes_exp: string;
  household_finance_exp: string;
  incr_large_expense: YesNo;
  incr_large_expense_exp: string;
  transferred_assets: YesNo;
  transferred_assets_exp: string;
  // Part IV — Household size + monthly income/expenses (assets = shared store)
  adults: string;
  children: string;
  month_gifts: string;
  month_wages: string;
  month_pensions: string;
  month_unemployment: string;
  month_social_security: string;
  month_government_assistant: string;
  month_alimony: string;
  month_child_support: string;
  month_self_employment_business_income: string;
  month_rental_income: string;
  month_interest_dividend: string;
  other_income_type1: string; other_income_type_val1: string;
  other_income_type2: string; other_income_type_val2: string;
  other_income_type3: string; other_income_type_val3: string;
  month_exp_food: string;
  month_exp_house_supply: string;
  month_exp_clothing_services: string;
  month_exp_personel_care: string;
  month_exp_auto_loan: string;
  month_exp_pub_trans: string;
  month_exp_rent_mort: string;
  month_exp_real_estate: string;
  month_exp_electric_gas: string;
  month_exp_telephone: string;
  month_exp_cab_int: string;
  month_exp_health: string;
  month_exp_out_pocket: string;
  month_exp_other_child_dep: string;
  month_exp_other_care: string;
  month_exp_other_income_tax: string;
  month_exp_other_estimate_tax: string;
  month_exp_other_term_insur: string;
  month_exp_other_retr_emp: string;
  month_exp_other_retr_volt: string;
  month_exp_other_union: string;
  month_exp_other_unpaid_local: string;
  month_exp_other_stu_loan: string;
  month_exp_type1: string; month_exp_type_val1: string;
  month_exp_type2: string; month_exp_type_val2: string;
  month_exp_type3: string; month_exp_type_val3: string;
  month_exp_misc: string;
  // Part V — Abuse / domestic violence (sensitive)
  sp_family_victim: YesNo;
  remove_code: boolean;
  abuse_explain: string;
  afraid_of_persion: YesNo;
  pose_danger: YesNo;
  // Part VI — Any other information
  any_other_information: string;
  // Part VII — Refund election
  check_box_refund: boolean;
}

// --- 433 family editable payloads -------------------------------------------
// snake_case keys mirror the backend mappers' `data` exactly (passed verbatim).

/** Filer selector shared by the 433 forms (Taxpayer / Spouse / Jointly). */
export type FileAs = "T" | "S" | "J";

export const FILE_AS_OPTIONS: { value: FileAs; label: string }[] = [
  { value: "T", label: "Taxpayer" },
  { value: "S", label: "Spouse" },
  { value: "J", label: "Jointly" },
];

/** Form 433-D — Installment Agreement (editor). Mirrors form_433d.py `data`. */
export interface Form433DData {
  file_as: FileAs;
  tax_forms: string;
  tax_periods: string;
  tax_due: string;
  tax_payment: string;
  first_pmt_date: string;
  monthly_payment: string;
  payment_day: string;
  routing_number: string;
  account_number: string;
  no_electronic: boolean;
  increase_amt_1: string;
  increase_date_1: string;
  increase_amt_2: string;
  increase_date_2: string;
  // 433-D(SP) only — editable employer + bank institution (persisted; the
  // Spanish template has no widgets for them, so they don't print).
  tp_employer?: string;
  tp_address_1?: string;
  tp_city?: string;
  tp_state?: string;
  tp_zip_code?: string;
  bank_name?: string;
  bank_street?: string;
  bank_csz?: string;
}

/** Form 433-H — combined CIS + IA header (editor). Mirrors form_433h.py `data`. */
export interface Form433HData {
  for_who: FileAs;
  tax_form_no: string;
  tax_periods: string;
  total_amount: string;
  add_tax_form_no: string;
  add_tax_periods: string;
  add_total_amount: string;
  tax_amt_pay: string;
  tax_pay_per_month: string;
  tax_pay_date: string;
  no_electronic: boolean;
  payroll_deduction: boolean;
  routing_number: string;
  account_number: string;
}

/** A personal bank account offered by the 433-D "import from 433-A" action. */
export interface ImportableBankAccount {
  id: string;
  institution: string;
  accountType: string;
  masked: string;
}

// --- Installment-Agreement request family (milestone 03-3) -------------------
// snake_case keys mirror the backend mappers' `data` exactly (passed verbatim).

/** Weekly / Bi-weekly / Semi-monthly / Monthly pay period (9465 Part II). */
export type PayPeriod = "" | "W" | "B" | "S" | "M";

export const PAY_PERIOD_OPTIONS: { value: PayPeriod; label: string }[] = [
  { value: "W", label: "Weekly" },
  { value: "B", label: "Bi-weekly" },
  { value: "S", label: "Semi-monthly" },
  { value: "M", label: "Monthly" },
];

/** Form 9465 — Installment Agreement Request (editor). Mirrors form_9465.py `data`. */
export interface Form9465Data {
  for_who: FileAs;
  tax_form_no: string;
  tax_periods: string;
  total_amount: string;
  add_total_amount: string;
  tax_amt_pay: string;
  tax_pay_per_month: string;
  tax_pay_date: string;
  best_time_home: string;
  best_time_work: string;
  new_address: boolean;
  for_business: boolean;
  business_name: string;
  ein: string;
  routing_number: string;
  account_number: string;
  no_electronic: boolean;
  payroll_deduction: boolean;
  // Part II — Additional Information ($25k–$50k / below-minimum cases).
  county: string;
  share_expenses: boolean;
  dependents: string;
  over_65: string;
  pay_period: PayPeriod;
  net_income: string;
  sp_pay_period: PayPeriod;
  sp_net_income: string;
  vehicles: string;
  car_payments: string;
  health_insurance: boolean;
  health_deducted: YesNo;
  health_premium: string;
  court_payments: boolean;
  court_deducted: YesNo;
  court_amount: string;
  child_care: string;
}

/** Weekly / Two-week / Monthly / Other frequency (2159). */
export type PayrollFrequency = "" | "W" | "T" | "M" | "O";

export const PAYROLL_FREQUENCY_OPTIONS: { value: PayrollFrequency; label: string }[] = [
  { value: "W", label: "Weekly" },
  { value: "T", label: "Every two weeks" },
  { value: "M", label: "Monthly" },
  { value: "O", label: "Other" },
];

/** Form 2159 — Payroll Deduction Agreement (editor). Mirrors form_2159.py `data`. */
export interface Form2159Data {
  for_who: FileAs;
  tax_form_no: string;
  tax_periods: string;
  total_amount: string;
  amt_owed_date: string;
  tax_pay_per_month: string;
  first_pmt_date: string;
  no_electronic: boolean;
  employer_name: string;
  employer_address: string;
  contact_name: string;
  contact_phone: string;
  ded_frequency: PayrollFrequency;
  ded_other: string;
  paid_frequency: PayrollFrequency;
  paid_other: string;
  sent_by_date: string;
  begin_on_date: string;
  emp_title: string;
  emp_date: string;
  write_add: string;
  deduc_until: string;
  title_corp_part: string;
  increase_amt_1: string;
  increase_date_1: string;
  increase_amt_2: string;
  increase_date_2: string;
}

/** Estimated installment-agreement payment from the Tier 2.2 calc (9465). */
export interface IaEstimate {
  monthlyPayment: string;
  iaType: string;
  iaTypeLabel: string;
  totalDue: string;
  calculatedMonths: number;
  missingCseds: number[];
  expiredCseds: number[];
}

/** Privilege rows for the fill UI. `sign_absent` is PIT (individual) only. */
export const FORM_3520_PRIVILEGES: {
  key: keyof Form3520Privileges;
  label: string;
  businessToo: boolean;
}[] = [
  { key: "add_rep", label: "Authorize the representative(s) to add another representative", businessToo: true },
  { key: "sign_absent", label: "Authorize the representative(s) to sign a return (taxpayer only)", businessToo: false },
  { key: "receive_check", label: "Receive (but not endorse or cash) refund checks", businessToo: true },
  { key: "waive_sol", label: "Waive the California statute of limitations", businessToo: true },
  { key: "exec_settlements", label: "Execute settlement and closing agreements", businessToo: true },
  { key: "other_priv", label: "Other acts (describe below)", businessToo: true },
  { key: "online_access", label: "Authorize MyFTB full online account access", businessToo: true },
];
